import React from "react";
import { Transition } from "@headlessui/react";
import clsx from "clsx";

import NOAStatus from "components/NOAStatus";
import {
  ClipboardClockSVGIcon,
  EmailAlertSVGIcon,
  UnfoldLessSVGIcon,
  UnfoldMoreSVGIcon
} from "components/SVGIcons";
import useDeviceType from "hooks/useDeviceType";
import { useUserFeature } from "hooks/useUserFeature";
import { IFRCalculationResult } from "models/HaulPay";
import BrokerFrStatus from "./Status/BrokerFrStatus";
import CarrierFrStatus from "./Status/CarrierFrStatus";
import FrStatusForCarrier from "./Status/FrStatusForCarrier";
import styles from "./style.module.scss";

interface IProps {
  isOpen: boolean;
  toggleAccordion: VoidFunction;
  selectedFR?: any;
  frCalculation?: IFRCalculationResult;
}

type StatusResponse = {
  status: string;
  className: string;
};

const getStatus = (status: string): StatusResponse => {
  switch (status) {
    case "pending":
      return { status: "Pending", className: "text-yellow-500" };
    case "incomplete":
      return { status: "Incomplete", className: "text-red-600" };
    case "approved":
      return { status: "Approved", className: "text-green-500" };
    case "doc_issue":
      return { status: "Doc Issue", className: "text-red-600" };
    case "declined":
      return { status: "Declined", className: "text-red-600" };
    case "paid":
      return { status: "Paid", className: "text-red-600" };
    default:
      return { status: "Pending", className: "text-yellow-500" };
  }
};

const getStatusClass = (relation: any) => {
  if (relation?.noaActive) {
    return "!text-green-500";
  } else if (relation?.noaSent) {
    return "!text-blue-500";
  } else if (relation?.noaRequested) {
    return "!text-amber-800";
  } else if (relation?.creditApproved === "Approved") {
    return "!text-red-500";
  } else {
    return "!text-red-500";
  }
};

export default function FRStatus({
  selectedFR,
  frCalculation,
  isOpen,
  toggleAccordion
}: Readonly<IProps>) {
  const { isBroker } = useUserFeature();
  const { isDesktop } = useDeviceType();

  return (
    <div className={styles.fundingRequestStatusAccordion}>
      <button type="button" className={styles.toggleButton} onClick={toggleAccordion}>
        {isOpen ? <UnfoldLessSVGIcon /> : <UnfoldMoreSVGIcon />}
      </button>
      <Transition
        as={React.Fragment}
        show={!isOpen}
        enter="transition-all duration-100 delay-400"
        enterFrom="opacity-0 hidden"
        enterTo="opacity-100 hidden"
        leave="duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0">
        <button
          type="button"
          className="w-full text-left focus:outline-none desktop:hidden"
          onClick={toggleAccordion}>
          <span>Expand Status</span>
        </button>
      </Transition>
      <Transition
        show={isDesktop ? true : isOpen}
        as="div"
        className="overflow-hidden transition-all max-h-fit"
        enter="duration-500"
        enterFrom="opacity-0 h-0"
        enterTo="opacity-100 h-[31.5rem]"
        leave="duration-500"
        leaveFrom="opacity-100 h-[31.5rem]"
        leaveTo="opacity-0 h-0">
        {isBroker ? (
          <>
            <ul className={styles.status}>
              <li>
                <ClipboardClockSVGIcon className={styles.clipboardIcon} />
                <div>
                  <span className="text-sm">Carrier’s Pay Status</span>
                  <span
                    className={clsx(
                      "uppercase text-textIcon",
                      getStatus(selectedFR?.carrierPaymentStatus)?.className
                    )}>
                    {selectedFR?.carrierPaymentStatus}
                  </span>
                </div>
              </li>
              <li>
                <ClipboardClockSVGIcon className={styles.clipboardIcon} />
                <div>
                  <span className="text-sm">Fuel Advance Status</span>
                  <span className={clsx("uppercase text-textIcon", getStatus("")?.className)}>
                    PENDING
                  </span>
                </div>
              </li>
              <li>
                <ClipboardClockSVGIcon className={styles.clipboardIcon} />
                <div>
                  <span className="text-sm">Your Margin Status</span>
                  <span
                    className={clsx(
                      "uppercase text-textIcon",
                      getStatus(selectedFR?.brokerMarginStatus)?.className
                    )}>
                    {selectedFR?.brokerMarginStatus}
                  </span>
                </div>
              </li>
              <li>
                <EmailAlertSVGIcon
                  className={clsx(
                    styles.emailAlertIcon,
                    getStatusClass(selectedFR?.factoringDebtorRelation)
                  )}
                />
                <div>
                  <span className="text-sm">NOA w/Debtor Status</span>
                  <NOAStatus
                    {...selectedFR?.factoringDebtorRelation}
                    debtorId={selectedFR?.debtor?.id}
                    factoringId={selectedFR?.factoringId}
                  />
                </div>
              </li>
            </ul>

            <div
              className={clsx(
                styles.cardBoxContainer,
                "desktop:!grid desktop:!grid-cols-2 desktop:!gap-6"
              )}>
              <CarrierFrStatus frCalculation={frCalculation} />

              <BrokerFrStatus frCalculation={frCalculation} />
            </div>
          </>
        ) : (
          <FrStatusForCarrier selectedFR={selectedFR} />
        )}
      </Transition>
    </div>
  );
}
